import React, { useEffect, useState } from 'react';
import { Header, Table, Message, Button, Toast, Modal } from 'react-frontier';
import { addCommas } from '../SiteUtil';
import classNames from 'classnames';
import API from '../API';

interface PayoutUser{
	user_id: number,
	name: string,
	amount: number,
	has_account: boolean,
}

interface ModalProps{
	open: boolean,
	payout_id: number,
	users: PayoutUser[],
	onClose: ()=>void,
	onPaid: (user_ids: number[])=>void,
}

var EmploymentPayoutStpModal = (props: ModalProps)=>{
	var [selected, setSelected] = useState<number[]>([]);
	var [downloaded, setDownloaded] = useState<boolean>(false);
	var [loading, setLoading] = useState<boolean>(false);
	var [prompts, setPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(!props.open) return;
		setSelected(props.users.map(u => u.user_id));
		setDownloaded(false);
		setLoading(false);
		setPrompts(null);
	}, [props.open]);

	var toggle = (user_id: number)=>{
		setDownloaded(false);
		setSelected(prev=>{
			var arr = [...prev];
			var ix = arr.indexOf(user_id);
			if(ix === -1) arr.push(user_id);
			else arr.splice(ix, 1);
			return arr;
		});
	};

	var isPayable = (u: PayoutUser)=> u.has_account && (u.amount || 0) > 0;
	var selectedUsers = props.users.filter(u => selected.indexOf(u.user_id) !== -1);
	var payableUsers = selectedUsers.filter(isPayable);
	var excludedUsers = selectedUsers.filter(u => !isPayable(u));
	var total = payableUsers.reduce((a, u)=> a + (u.amount || 0), 0);

	var download = ()=>{
		setPrompts(null);
		API.downloadEmploymentPayoutStp(props.payout_id, selected).then(()=>{
			setDownloaded(true);
		}).catch(()=> setPrompts(['Hubo un error descargando el archivo STP (STP-1)']));
	};

	var confirm = ()=>{
		var payable = payableUsers.map(u => u.user_id);
		if(payable.length === 0) return setPrompts(['Ningún empleado seleccionado entró en el archivo STP.']);
		setLoading(true);
		setPrompts(null);
		API.payEmploymentPayout(props.payout_id, payable, true).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Pagos realizados!');
			props.onPaid(payable);
		}).catch(()=> setPrompts(['Hubo un error marcando los pagos (PAY-1)']))
		.finally(()=> setLoading(false));
	};

	return <Modal open={props.open} size='small' onClose={props.onClose}>
		<Modal.Header>Descargar STP</Modal.Header>
		<Modal.Content style={{ maxHeight: 700, overflowY: 'auto', overflowX: 'hidden' }}>
			<Header
				text={downloaded ? 'Confirmar descarga' : '¿Descargar archivo STP?'}
				subtext={downloaded
					? 'Verifica que el archivo no tenga errores. Al confirmar se marcarán como pagados los empleados seleccionados.'
					: 'Selecciona los empleados a incluir en el archivo STP.'}
			/>
			<div style={{ textAlign: 'center' }}>
				<Button text='Seleccionar todos' color='black' onClick={()=>{
					setDownloaded(false);
					setSelected(selected.length === props.users.length ? [] : props.users.map(u => u.user_id));
				}} />
			</div>
			<Table
				selectable fitted
				style={{ marginTop: 15 }}
				headers={[
					<div style={{ textAlign: 'center' }}><i className='checkmark icon' style={{ marginRight: 0 }}></i></div>,
					'Empleado',
					'Monto',
					'Cuenta'
				]}
				collapsingIndexes={[0, 2, 3]}
				centeredIndexes={[0, 2, 3]}
				onClick={(v, i)=> toggle(props.users[i].user_id)}
				data={props.users.map(u=>{
					var isSelected = selected.indexOf(u.user_id) !== -1;
					return [
						<i className={classNames('large icon circle', { check: isSelected, outline: !isSelected })}></i>,
						u.name,
						'$' + addCommas(u.amount || 0),
						u.has_account
							? <i className='icon check' style={{ color: '#21ba45' }}></i>
							: <i className='icon close' style={{ color: '#db2828' }}></i>
					];
				})}
			/>
			<div style={{ textAlign: 'right', fontWeight: 500, marginTop: 8 }}>
				Total: ${addCommas(total)}
			</div>
			{excludedUsers.length > 0 && (
				<Message type='warning' centered header='Empleados fuera del archivo'>
					<div style={{ fontSize: 14 }}>
						{excludedUsers.length} empleado(s) no entran en el archivo STP ni se marcarán como pagados, por no tener cuenta bancaria o quedar en cero tras sus ajustes: {excludedUsers.map(m => m.name).join(', ')}
					</div>
				</Message>
			)}
			<Message type='error' list={prompts} />
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cerrar' basic onClick={props.onClose} />
			{downloaded ? (
				<Button text='Confirmar descarga' color='black' loading={loading} onClick={confirm} />
			) : (
				<Button text='Descargar STP' color='black' iconName='download' disabled={selected.length === 0} onClick={download} />
			)}
		</Modal.Actions>
	</Modal>;
}

export default EmploymentPayoutStpModal;
